/**
 * The small link beside a «CSV» button that downloads the OTHER CSV dialect (`docs/adr/9072`, addendum of 2026-10-23).
 *
 * The «CSV» button writes the dialect the computer's regional number format wants (`src/export/regional.ts`); this link writes the
 * other one and says which it is, in words a person reads — not a value that lives in a tooltip (`docs/adr/9070` R17): «altro formato
 * CSV: CSV con virgola decimale (Excel in italiano/spagnolo)» or «altro formato CSV: CSV grezzo (punto decimale, per altri sistemi)».
 * It is a quiet link, not a third button: the common case is the button beside it.
 */

import { useTranslation } from 'react-i18next';

import type { CsvFormat } from '../export/regional';
import { FOCUS_RING } from '../primitives';

/** Each CSV format's words in the link, and its tooltip (the longer explanation). */
export const OTHER_CSV_WORDS: Readonly<Record<CsvFormat, { readonly link: string; readonly hint: string }>> = {
  'csv-decimal-comma': { link: 'export.csvOther.decimalComma', hint: 'export.decimalComma.hint' },
  csv: { link: 'export.csvOther.raw', hint: 'export.csvRaw.hint' },
};

export interface OtherCsvLinkProps {
  /** The format the link writes — the CSV the button beside it does NOT write. */
  readonly format: CsvFormat;
  readonly onDownload: () => void;
  /** This link's file is being prepared: its words say so. */
  readonly busy?: boolean;
  /** Another download is under way. */
  readonly disabled?: boolean;
  /** The words while the file is prepared (the screen's own for this format). */
  readonly preparing: string;
  /**
   * What the file holds, translated — the same words the «CSV» button beside it says after «Scarica CSV:» (`docs/adr/9074`):
   * the two CSVs are one table, so a button that says «23.636 righe · 3.289 righe di negozi scartati escluse» has a link that
   * says it too. Absent where the CSV button names no rows.
   */
  readonly holds?: string;
  readonly [attribute: `data-${string}`]: string | undefined;
}

export function OtherCsvLink({ format, onDownload, busy = false, disabled = false, preparing, holds, ...data }: OtherCsvLinkProps): React.JSX.Element {
  const { t } = useTranslation();
  const words = OTHER_CSV_WORDS[format];
  const label = holds === undefined ? t(words.link) : t('export.csvOther.holding', { format: t(words.link), holds });
  return (
    <button
      type="button"
      onClick={onDownload}
      // Its own file being prepared disables it too: a second press would start a second download (`docs/adr/9074`).
      disabled={disabled || busy}
      aria-busy={busy}
      title={t(words.hint)}
      data-other-csv=""
      className={`text-ink-secondary hover:text-ink-primary inline-flex min-h-[24px] items-center text-caption underline underline-offset-2 disabled:no-underline disabled:opacity-50 ${FOCUS_RING}`}
      {...data}
    >
      {busy ? t(preparing) : label}
    </button>
  );
}
